import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  readSession, writeSession, clearSession, updateSessionUser,
  migrateLegacySession, roleFromPath,
} from '../utils/session';
import { clearOfflineCache } from '../utils/offline';

const AuthContext = createContext(null);

// Holds one session per role and exposes the one that matches the current
// URL as { user, role }. Must be rendered inside the Router (it reads the
// location to pick the active role).
export const AuthProvider = ({ children }) => {
  const location = useLocation();
  const [sessions, setSessions] = useState(() => {
    migrateLegacySession();
    return { coach: readSession('coach'), parent: readSession('parent') };
  });
  const [loading, setLoading] = useState(false);

  // Another tab signing in/out should be reflected here.
  useEffect(() => {
    const onStorage = () => setSessions({ coach: readSession('coach'), parent: readSession('parent') });
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const pathRole = roleFromPath(location.pathname);
  const role = pathRole
    ? (sessions[pathRole] ? pathRole : null)
    : (sessions.coach ? 'coach' : sessions.parent ? 'parent' : null);
  const user = role ? sessions[role].user : null;

  const loginCoach = useCallback((token, coachData) => {
    writeSession('coach', token, coachData);
    setSessions((s) => ({ ...s, coach: { token, user: coachData } }));
  }, []);

  const loginParent = useCallback((token, familyData) => {
    writeSession('parent', token, familyData);
    setSessions((s) => ({ ...s, parent: { token, user: familyData } }));
  }, []);

  // Patch cached fields of the active role's user (name, programName, ...).
  const updateUser = useCallback((patch) => {
    if (!role) return;
    const updated = updateSessionUser(role, patch);
    if (updated) setSessions((s) => ({ ...s, [role]: { ...s[role], user: updated } }));
  }, [role]);

  const updateChildCount = useCallback((count) => {
    const updated = updateSessionUser('parent', { childCount: count });
    if (updated) setSessions((s) => ({ ...s, parent: { ...s.parent, user: updated } }));
  }, []);

  // Signs out ONLY the active role; the other role's session survives.
  const logout = useCallback(() => {
    const target = role || pathRole;
    if (!target) return;
    clearSession(target);
    clearOfflineCache(); // X-20
    setSessions((s) => ({ ...s, [target]: null }));
  }, [role, pathRole]);

  const value = useMemo(() => ({
    user, role, loading, sessions, setLoading,
    loginCoach, loginParent, updateUser, updateChildCount, logout,
  }), [user, role, loading, sessions, loginCoach, loginParent, updateUser, updateChildCount, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
